import { FC, Fragment } from "react";
import { IoMdCheckmark } from "react-icons/io";
import { JudgeInsuranceSectionData } from "src/dtos/applicationData";
import { handleYesNoAnswers } from "src/utils/formDataManipulation";
import { numberToCurrency } from "src/utils/numberFormat";
import { SectionArticlesTitle } from "./styles";
import { TbFileCheck } from 'react-icons/tb'

interface JudgeInsuranceSectionProps {
  insuranceSection?: JudgeInsuranceSectionData
  isComplete: boolean
}

const JudgeInsuranceSection: FC<JudgeInsuranceSectionProps> = ({ insuranceSection, isComplete }) => {
  return (
    <section>
      <header>
        <h4>Insurance</h4>
        {isComplete ? (
          <h6><IoMdCheckmark /> Complete</h6>
        ) : null}
      </header>
      <SectionArticlesTitle>INSURANCE HISTORY</SectionArticlesTitle>
      <article>
        <strong>Has applicant been insured within the last 4 years?</strong>
        <span>{handleYesNoAnswers(insuranceSection?.hasBeenInsuredRecently)}</span>
      </article>
      {insuranceSection?.hasBeenInsuredRecently === true ? (
        <>
          {insuranceSection?.recentInsurances?.map((insurance, index) => (
            <article key={index}>
              <strong>{index === 0 ? 'Most Recent Insurance' : 'Previous Insurance'}</strong>
              <span>
                <h6 style={{ marginTop: 0 }}>Insurance Company: </h6>
                {insurance?.insuranceCompany ?? 'N/A'}
              </span>
              <span>
                <h6>Limits of Liability: </h6>
                {insurance?.liabilityLimits ?? 'N/A'}
              </span>
              <span>
                <h6>Effective Dates: </h6>
                {(!!insurance?.effectiveDateFrom && !!insurance?.effectiveDateTo) ? (
                  <>From {insurance?.effectiveDateFrom} to {insurance?.effectiveDateTo}</>
                ) : (
                  <>N/A</>
                )}
              </span>
            </article>
          ))}
        </>
      ) : null}
      <SectionArticlesTitle>PAST LIABILITY</SectionArticlesTitle>
      <article>
        <strong>Has the applicant ever had similar insurance canceled, declined or non-renewed?</strong>
        <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasInsuranceDeclined)}</span>
        {insuranceSection?.pastLiability?.hasInsuranceDeclined === true ? (
          <span>{insuranceSection?.pastLiability?.insuranceDeclinedDetails ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>Has the applicant ever had any professional liability claims asserted or action filed against him or her,
          or has a claim been paid on behalf of the applicant?</strong>
        <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasClaimsAsserted)}</span>
      </article>
      <article>
        <strong>
          Has the applicant ever been reprimanded or refused admission to practice before any court or administrative
          agency or otherwise been disbarred or had his or her rights to practice suspended (including voluntary suspension)?
        </strong>
        <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasBeenRefused)}</span>
        {insuranceSection?.pastLiability?.hasBeenRefused === true ? (
          <span>{insuranceSection?.pastLiability?.refusedDetails ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>Has the applicant ever been reprimanded or had his or her rights to serve as a judge suspended (including voluntary suspension)?</strong>
        <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasBeenSuspended)}</span>
        {insuranceSection?.pastLiability?.hasBeenSuspended === true ? (
          <span>{insuranceSection?.pastLiability?.suspendedDetails ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>Does the applicant know of any circumstance, act, error or omission that might form the basis of a claim against the applicant?</strong>
        <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasActsToBaseClaim)}</span>
      </article>
      {(insuranceSection?.claims && insuranceSection.claims.length > 0) ?
        insuranceSection.claims.map((claim, index) => (
          <Fragment key={index + 1}>
            <SectionArticlesTitle>CLAIM {index + 1}</SectionArticlesTitle>
            <article>
              <strong>Firm Name</strong>
              <span>{claim?.firmName ?? 'N/A'}</span>
            </article>
            <article>
              <strong>Claimant's Full Name</strong>
              <span>{claim?.hasClaimant === false ? "I'm not sure" : (claim?.claimantFullName ?? 'N/A')}</span>
            </article>
            <article>
              <strong>Claim Type</strong>
              <span>{claim?.claimType ?? 'N/A'}</span>
            </article>
            <article>
              <strong>Date of Claim</strong>
              <span>{claim?.hasClaimDate === false ? "I'm not sure" : (claim?.claimDate ?? 'N/A')}</span>
            </article>
            <article>
              <strong>Date of Alleged Error</strong>
              <span>{claim?.hasAllegedErrorDate === false ? "I'm not sure" : (claim?.allegedErrorDate ?? 'N/A')}</span>
            </article>
            <article>
              <strong>Is this claim closed?</strong>
              <span>{claim?.isClaimClosed ?? 'N/A'}</span>
              {claim?.isClaimClosed === 'Yes' ? (
                <>
                  <span>
                    <h6>Indicate how the claim was closed: </h6>
                    {claim?.closingType ?? 'N/A'}
                  </span>
                  <span>
                    <h6>Total loss paid including deductibles: </h6>
                    {claim?.totalLossPaid ? numberToCurrency(claim?.totalLossPaid) : 'N/A'}
                  </span>
                  <span>
                    <h6>Defense cost paid: </h6>
                    {claim?.defenseCostPaid ? numberToCurrency(claim?.defenseCostPaid) : 'N/A'}
                  </span>
                </>
              ) : claim?.isClaimClosed === 'No' ? (
                <>
                  <span>
                    <h6>Claimant's settlement demand: </h6>
                    {claim?.claimantsSettlementDemand ? numberToCurrency(claim?.claimantsSettlementDemand) : 'N/A'}
                  </span>
                  <span>
                    <h6>Defendant's offer for settlement: </h6>
                    {claim?.defendantsSettlementOffer ? numberToCurrency(claim?.defendantsSettlementOffer) : 'N/A'}
                  </span>
                  <span>
                    <h6>Insurer's loss reserve: </h6>
                    {claim?.insurersLossReserve ? numberToCurrency(claim?.insurersLossReserve) : 'N/A'}
                  </span>
                  <span>
                    <h6>Deductible: </h6>
                    {claim?.closingCaseDeductible ? numberToCurrency(claim?.closingCaseDeductible) : 'N/A'}
                  </span>
                  <span>
                    <h6>Is claim in suit?: </h6>
                    {claim?.isClaimInSuit ?? 'N/A'}
                  </span>
                  {claim?.isClaimInSuit === 'Yes' ? (
                    <span>
                      <h6>Amount asked in summons: </h6>
                      {claim?.amountAskedSummons ? numberToCurrency(claim?.amountAskedSummons) : 'N/A'}
                    </span>
                  ) : null}
                  <span>
                    <h6>Total loss pain including deductibles: </h6>
                    {claim?.totalLossPain ? numberToCurrency(claim?.totalLossPain) : 'N/A'}
                  </span>
                </>
              ) : null}
            </article>
            <article>
              <strong>Name of Insurer</strong>
              <span>{claim?.hasInsurer === false ? "I'm not sure" : (claim?.insurerName ?? 'N/A')}</span>
            </article>
            <article>
              <strong>Description of Claim</strong>
              <span>
                <h6>Alleged act, error, or omission upon which claimant bases claim:</h6>
              </span>
              <span>{claim?.hasClaimDescription === false ? "I'm not sure" : (claim?.claimDescription ?? 'N/A')}</span>
              <span>
                <h6>Description of case and events, including area of practice out of which the claim arose:</h6>
              </span>
              <span>{claim?.hasCaseDescription === false ? "I'm not sure" : (claim?.caseDescription ?? 'N/A')}</span>
              <span>
                <h6>Description of the type and extent of injury or damage allegedly sustained:</h6>
              </span>
              <span>{claim?.hasDamageDescription === false ? "I'm not sure" : (claim?.damageDescription ?? 'N/A')}</span>
              <span>
                <h6>Upload any applicable claim information:</h6>
              </span>
              {claim?.claimFile?.map(file => (
                <span key={file?.id} className="icon"><TbFileCheck/>{file?.fileName ?? 'N/A'}</span>
              ))}
            </article>
          </Fragment>
        )) : null}
      <SectionArticlesTitle>REVIEW</SectionArticlesTitle>
      <article>
        <strong>Notes</strong>
        <span>{insuranceSection?.review?.note ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Files</strong>
        {!!insuranceSection?.review?.files && insuranceSection?.review?.files.length > 0 ? 
          insuranceSection?.review?.files.map(file => (
            <span key={file?.id} className="icon"><TbFileCheck /> {file?.fileName ?? "N/A"}</span>
          )) : (
            <span>N/A</span>
          )}
      </article>
    </section>
  )
}

export default JudgeInsuranceSection